import React, { Component } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import {
  Container,
  Button,
  Row,
  Column,
  Card,
  CardContent,
  CardSpacing,
  TinyLink
} from './CommonUiComponents';
import StatusWrapper from './StatusWrapper';
import StatusLoader from './StatusLoader';
import { textGray } from '../utils/colors';
import apiRequest from '../utils/apiRequest';
import NoBook from './NoBook';
import findIdByPath from '../utils/findIdByPath';
import { LoginLink, LoginButtonWrapper } from '../style-guide/Login';

const LoginContainer = Container.extend`
  margin: 0;
  margin-left: auto;
`;

const TopSection = styled.div`
  margin: 0 auto;
  position: relative;
  margin-top: 4rem;

  @media all and (max-width: 1024px) {
    margin-top: 3rem;
    text-align: center;
  }
`;

const RegisterCard = styled(Card)`
  background-color: #ffffff;
`;

const BookTitle = styled.h1`
  font-size: 4rem;
  line-height: 2.8125rem;
  color: ${textGray};
  margin-top: 0.75rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};

  @media all and (max-width: 40rem) {
    font-size: 2rem;
  }
`;

const BookCategory = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  color: ${textGray};
  font-weight: 400;
  letter-spacing: 1px;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const LoginForm = styled.form`
  width: 100%;
`;

const InputField = styled.input`
  width: 370px;
  display: block;
  height: 70px;
  border-radius: 10px;
  border: 1px solid;
  border-color: ${textGray};
  padding-left: 1.5625rem;
  font-size: 1.3rem;
  font-style: italic;
  font-weight: 300;
  background: transparent;
  color: ${textGray};
  position: relative;
  outline: none;
  transition: border-color 0.2s ease-in-out;
  margin-bottom: 1.5rem;

  ${props =>
    props.inverted &&
    css`
      border-color: #ffffff;
    `};

  @media all and (max-width: 776px) {
    width: 100%;
    height: 50px;
  }

  &:focus,
  &:hover {
    border-color: ${props => props.theme.tertiary};
  }
`;

const InputLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  color: ${textGray};
  display: block;
  margin-bottom: 0.5rem;
  text-transform: uppercase;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const FormTitle = styled.h2`
  color: ${textGray};
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.25rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const ErrorText = styled.p`
  color: ${textGray};

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const SuccessMessage = styled.h2`
  color: ${textGray};
  font-size: 1.25rem;

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

const StyledAnchor = TinyLink.extend`
  color: ${textGray};
  text-decoration: underline;
  font-family: 'Source Serif Pro';
  font-weight: normal;
  font-size: 1.25rem;

  &:hover {
    color: ${props => props.theme.tertiary};
  }

  ${props =>
    props.inverted &&
    css`
      color: #ffffff;
    `};
`;

export default class Register extends Component {
  state = {
    firstName: '',
    lastName: '',
    password: '',
    matchPassword: '',
    phone: '',
    email: '',
    workPlace: '',
    license: '',
    registering: false,
    bookData: null,
    fetchingBookData: false,
    isPasswordsMatching: true,
    wrongLincenseKey: false,
    registerSuccess: false
  };

  componentDidMount() {
    this.fetchBookData();
    this.setBodyThemeColor();
  }

  componentDidUpdate() {
    this.setBodyThemeColor();
  }

  setBodyThemeColor() {
    const body = document.body;
    const { bookData } = this.state;

    if (bookData && bookData.theme) {
      body.style.backgroundColor = bookData.theme.primary;
    }
  }

  async fetchBookData() {
    const {
      match: { params },
      setInvertedTheme,
      unsetInvertedTheme
    } = this.props;
    this.setState({ fetchingBookData: true });

    const response = await apiRequest(
      'wordpress',
      `bookdata/v1/login?id=${params.bookId}`
    );

    if (response.theme.text_color === 'dark') {
      setInvertedTheme();
    } else {
      unsetInvertedTheme();
    }

    this.setState({
      fetchingBookData: false,
      bookData: response
    });
  }

  submitUserForm = async e => {
    const {
      firstName,
      lastName,
      password,
      matchPassword,
      phone,
      email,
      workPlace,
      license
    } = this.state;

    e.preventDefault();

    if (password !== matchPassword) {
      this.setState({
        isPasswordsMatching: false
      });

      return false;
    } else {
      this.setState({
        isPasswordsMatching: true
      });
    }

    this.setState({ registering: true });

    const response = await apiRequest('wordpress', 'userdata/v1/add', 'POST', {
      registerkey: license,
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      phone,
      institution: workPlace
    });

    this.setState({ registering: false });

    if (
      response.code === 'incorrect_registerkey_given' ||
      response.code === 'used_registerkey_given'
    ) {
      this.setState({
        wrongLincenseKey: true,
        registerSuccess: false
      });
    }

    if (response.code === 'user_created') {
      this.setState({
        registerSuccess: true
      });
    }
  };

  handleInputValue = e => {
    const { name, value } = e.target;

    this.setState({
      [name]: value
    });
  };

  render() {
    const { invertedTheme } = this.props;
    const {
      bookData,
      fetchingBookData,
      registering,
      isPasswordsMatching,
      wrongLincenseKey,
      registerSuccess
    } = this.state;

    if (!bookData) return null;

    if (bookData.status === 404) {
      return (
        <NoBook inverted={invertedTheme}>
          Ojda, hva gjør du på denne URL'en?
        </NoBook>
      );
    }

    return (
      <ThemeProvider theme={bookData.theme}>
        <StatusLoader fullsize large busy={fetchingBookData}>
          <LoginContainer>
            <Row>
              <Column>
                <TopSection>
                  <BookCategory inverted={invertedTheme}>
                    {bookData.course_name}
                  </BookCategory>
                  <BookTitle inverted={invertedTheme}>
                    «{bookData.title}»
                  </BookTitle>
                </TopSection>
              </Column>
            </Row>
            <Row>
              <Column>
                <RegisterCard>
                  <CardSpacing>
                    <CardContent>
                      {registerSuccess ? (
                        <SuccessMessage inverted={invertedTheme}>
                          Registreringen var vellykket. {''}
                          <StyledAnchor
                            inverted={invertedTheme}
                            to={`/bok/${findIdByPath()}/logg-inn`}
                          >
                            Logg inn her
                          </StyledAnchor>
                        </SuccessMessage>
                      ) : (
                        <StatusWrapper busy={registering}>
                          <LoginForm
                            onSubmit={this.submitUserForm}
                            method="POST"
                          >
                            <FormTitle inverted={invertedTheme}>
                              Registrer deg for å få tilgang til boken og «Min
                              side»{' '}
                            </FormTitle>
                            <InputLabel
                              htmlFor={'firstName'}
                              inverted={invertedTheme}
                            >
                              Fornavn
                            </InputLabel>
                            <InputField
                              type="text"
                              name="firstName"
                              id="firstName"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'lastName'}
                              inverted={invertedTheme}
                            >
                              Etternavn
                            </InputLabel>
                            <InputField
                              type="text"
                              name="lastName"
                              id="lastName"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'password'}
                              inverted={invertedTheme}
                            >
                              Passord
                            </InputLabel>
                            <InputField
                              type="password"
                              name="password"
                              id="password"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'matchPassword'}
                              inverted={invertedTheme}
                            >
                              Passord på nytt
                            </InputLabel>
                            <InputField
                              type="password"
                              name="matchPassword"
                              id="matchPassword"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'phone'}
                              inverted={invertedTheme}
                            >
                              Telefon
                            </InputLabel>
                            <InputField
                              type="tel"
                              name="phone"
                              id="phone"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'email'}
                              inverted={invertedTheme}
                            >
                              E-post
                            </InputLabel>
                            <InputField
                              type="email"
                              name="email"
                              id="email"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'workPlace'}
                              inverted={invertedTheme}
                            >
                              Arbeidsplass
                            </InputLabel>
                            <InputField
                              type="text"
                              name="workPlace"
                              id="workPlace"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />
                            <InputLabel
                              htmlFor={'license'}
                              inverted={invertedTheme}
                            >
                              Lisensnøkkel
                            </InputLabel>
                            <InputField
                              type="text"
                              name="license"
                              id="license"
                              onChange={this.handleInputValue}
                              disabled={registering}
                              inverted={invertedTheme}
                              required
                            />

                            {!isPasswordsMatching && (
                              <ErrorText inverted={invertedTheme}>
                                Passordene matcher ikke.
                              </ErrorText>
                            )}
                            {wrongLincenseKey && (
                              <ErrorText inverted={invertedTheme}>
                                Lisensnøkkelen er feil. Pass på at du har
                                skrevet den riktig, eller at den ikke er blitt
                                brukt på en annen bruker.
                              </ErrorText>
                            )}
                            <LoginButtonWrapper>
                              <Button type="submit" disabled={registering}>
                                {registering ? 'Registrerer' : 'Registrer'}
                              </Button>
                              <LoginLink to="logg-inn">
                                Tilbake til logg inn
                              </LoginLink>
                            </LoginButtonWrapper>
                          </LoginForm>
                        </StatusWrapper>
                      )}
                    </CardContent>
                  </CardSpacing>
                </RegisterCard>
              </Column>
            </Row>
          </LoginContainer>
        </StatusLoader>
      </ThemeProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Register.js